// src/Router/routeLinks.tsx
import React from 'react';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import PaymentsIcon from '@mui/icons-material/Payments';
import Page from '../App/Layout/Page';
import { Dashboard } from '../Features/Dashboard';
import LoginPage from '../Features/Auth/pages/LoginPage';
import { CreateOffer, EditOffer, Offers } from '../Features/Offers';
import { CreateHotel, EditHotel, Hotels } from '../Features/Hotels';
import {
  AccountingExportsRoutes,
  BankAccountsRoutes,
  BankDocumentsRoutes,
  BookingsRoutes,
  CategoriesRoutes,
  CashDocumentsRoutes,
  CompaniesRoutes,
  ContractTemplatesRoutes,
  CountriesRoutes,
  DiscountsRoutes,
  HotelsRoutes,
  InvoicesRoutes,
  KsefOutgoingRoutes,
  ExpenseInvoicesRoutes,
  OfferOptionsRoutes,
  OffersRoutes,
  PartiesRoutes,
  RegionsRoutes,
  ReportsRoutes,
  ReservationsRoutes,
  TagsRoutes,
  UserManagementRoutes,
  AuthRoutes,
} from './path';
import { Countries, CreateCountry, EditCountry } from '../Features/Countries';
import { Categories, CreateCategory, EditCategory } from '../Features/Categories';
import { AccountingExportPage, Bookings, CreateBooking, EditBooking } from '../Features/Bookings';
import { AccountingExportsPage } from '../Features/AccountingExports';
import { CreateDiscount, Discounts, EditDiscount } from '../Features/Discounts';
import { CreateRegion, EditRegion, Regions } from '../Features/Regions';
import { CreateTag, EditTag, Tags } from '../Features/Tags';
import { CreateOfferOptions, OfferOptions, EditOfferOptions } from '../Features/OfferOptions';
import { Companies, CreateCompany, EditCompany } from '../Features/Companies';
import { CreateParty, EditParty, Parties } from '../Features/Parties';
import { BankAccounts, EditBankAccount, UpdateBankAccount } from '../Features/BankAccounts';
import {
  BankDocumentDetailsPage,
  BankDocumentQueuePage,
  BankDocumentsPage,
  SupplierPrepaymentApContractorsPage,
  SupplierPrepaymentLedgerPage,
  SupplierPrepaymentReconciliationPage,
} from '../Features/BankDocuments';
import { CreateReservation, Reservations, UpdateReservation } from '../Features/Reservation';
import {
  ContractTemplates,
  CreateContractTemplate,
  EditContractTemplate,
} from '../Features/ContractTemplates';
import {
  CreateInvoice,
  CreateCorrectionInvoice,
  EditInvoice,
  Invoices,
} from '../Features/Invoices';
import InvoiceImportPage from '../Features/InvoiceImport/Pages/InvoiceImportPage';
import ImportHistoryPage from '../Features/InvoiceImport/Pages/ImportHistoryPage';
import ImportJobDetailPage from '../Features/InvoiceImport/Pages/ImportJobDetailPage';
import ExpenseInvoicesList from '../Features/ExpenseInvoices/Page/ExpenseInvoicesList';
import { ExpenseInvoiceDetailsPage } from '../Features/ExpenseInvoices/Page/ExpenseInvoiceDetailsPage';
import CreateExpenseInvoicePage from '../Features/ExpenseInvoices/Page/CreateExpenseInvoicePage';
import EditExpenseInvoicePage from '../Features/ExpenseInvoices/Page/EditExpenseInvoicePage';
import {
  CashDocumentsPage,
  CashDocumentDetailPage,
  EditCashDocument,
  CreateCashDocument,
} from '../Features/CashDocuments';
import { UserManagementPage } from '../Features/UserManagement';
import {
  ReservationPaymentReconciliationPage,
  UnsettledBookingsAccountingReportPage,
  UninvoicedReservationPaymentsReportPage,
} from '../Features/Reports';
import VerifyEmailPage from '../Features/Auth/pages/VerifyEmailPage';
import ChangePasswordPage from '../Features/Auth/pages/ChangePasswordPage';
import { KsefOutgoingConsolePage } from '../Features/KsefOutgoing';
import {
  isBankDocumentsQueueEnabled,
  isSupplierPrepaymentsEnabled,
} from '../Features/BankDocuments/Utils/bankDocumentsFeatureFlags';

export interface RouteLink {
  label?: string;
  path: string;
  element: React.ReactNode;
  showInSidebar?: boolean;
  children?: RouteLink[];
  /** Icon rendered next to the label in the sidebar. Only used by grouped sidebar entries today. */
  icon?: React.ReactNode;
  /**
   * Groups this link under a collapsible sidebar section with this label, instead of showing it
   * as a flat top-level item. Does not affect routing — the route itself stays a flat sibling.
   */
  sidebarGroup?: string;
}

interface CrudRoutes {
  root: string;
  create: string;
  update: string;
}
interface CrudConfig {
  label: string;
  routes: CrudRoutes;
  list: React.ReactNode;
  create: React.ReactNode;
  update: React.ReactNode;
}

const crud = ({ label, routes, list, create, update }: CrudConfig): RouteLink[] => [
  { label, path: routes.root, element: list, showInSidebar: true },
  { path: routes.create, element: create, showInSidebar: false },
  { path: routes.update, element: update, showInSidebar: false },
];

// Sekcje CRUD zgrupowane dla czytelności
const featureSections: RouteLink[] = [
  ...crud({
    label: 'Oferty',
    routes: OffersRoutes,
    list: <Offers />,
    create: <CreateOffer />,
    update: <EditOffer />,
  }),
  ...crud({
    label: 'Rezerwacje',
    routes: BookingsRoutes,
    list: <Bookings />,
    create: <CreateBooking />,
    update: <EditBooking />,
  }),
  {
    label: 'Rozliczenia dla księgowości',
    path: AccountingExportsRoutes.root,
    element: <AccountingExportsPage />,
    showInSidebar: true,
  },
  {
    path: BookingsRoutes.accountingExport,
    element: <AccountingExportPage />,
    showInSidebar: false,
  },
  ...crud({
    label: 'Hotele',
    routes: HotelsRoutes,
    list: <Hotels />,
    create: <CreateHotel />,
    update: <EditHotel />,
  }),
  ...crud({
    label: 'Kraje',
    routes: CountriesRoutes,
    list: <Countries />,
    create: <CreateCountry />,
    update: <EditCountry />,
  }),
  ...crud({
    label: 'Kategorie',
    routes: CategoriesRoutes,
    list: <Categories />,
    create: <CreateCategory />,
    update: <EditCategory />,
  }),
  ...crud({
    label: 'Zniżki',
    routes: DiscountsRoutes,
    list: <Discounts />,
    create: <CreateDiscount />,
    update: <EditDiscount />,
  }),
  ...crud({
    label: 'Regiony',
    routes: RegionsRoutes,
    list: <Regions />,
    create: <CreateRegion />,
    update: <EditRegion />,
  }),
  ...crud({
    label: 'Tagi',
    routes: TagsRoutes,
    list: <Tags />,
    create: <CreateTag />,
    update: <EditTag />,
  }),
  ...crud({
    label: 'Opcje ofert',
    routes: OfferOptionsRoutes,
    list: <OfferOptions />,
    create: <CreateOfferOptions />,
    update: <EditOfferOptions />,
  }),
  ...crud({
    label: 'Firmy własne',
    routes: CompaniesRoutes,
    list: <Companies />,
    create: <CreateCompany />,
    update: <EditCompany />,
  }),
  ...crud({
    label: 'Strony (Parties)',
    routes: PartiesRoutes,
    list: <Parties />,
    create: <CreateParty />,
    update: <EditParty />,
  }),
  ...crud({
    label: 'Konta bankowe',
    routes: BankAccountsRoutes,
    list: <BankAccounts />,
    create: <EditBankAccount />,
    update: <UpdateBankAccount />,
  }),
  {
    label: 'Dokumenty bankowe',
    path: BankDocumentsRoutes.root,
    element: <BankDocumentsPage />,
    showInSidebar: true,
  },
  ...(isSupplierPrepaymentsEnabled
    ? [
        {
          label: 'Prepayment dostawców',
          path: BankDocumentsRoutes.supplierPrepayments,
          element: <SupplierPrepaymentLedgerPage />,
          showInSidebar: true,
        },
        {
          label: 'Rekoncyliacja prepayments',
          path: BankDocumentsRoutes.supplierPrepaymentReconciliation,
          element: <SupplierPrepaymentReconciliationPage />,
          showInSidebar: true,
        },
        {
          label: 'Kontrahenci AP',
          path: BankDocumentsRoutes.supplierPrepaymentApContractors,
          element: <SupplierPrepaymentApContractorsPage />,
          showInSidebar: true,
        },
      ]
    : []),
  ...(isBankDocumentsQueueEnabled
    ? [
        {
          label: 'Kolejka dokumentów bankowych',
          path: BankDocumentsRoutes.queue,
          element: <BankDocumentQueuePage />,
          showInSidebar: true,
        },
      ]
    : []),
  {
    path: BankDocumentsRoutes.details,
    element: <BankDocumentDetailsPage />,
    showInSidebar: false,
  },
  ...crud({
    label: 'Rezerwacje klientów',
    routes: ReservationsRoutes,
    list: <Reservations />,
    create: <CreateReservation />,
    update: <UpdateReservation />,
  }),
  ...crud({
    label: 'Szablony kontraktów',
    routes: ContractTemplatesRoutes,
    list: <ContractTemplates />,
    create: <CreateContractTemplate />,
    update: <EditContractTemplate />,
  }),
  // Faktury - with custom correction route
  { label: 'Faktury', path: InvoicesRoutes.root, element: <Invoices />, showInSidebar: true },
  { path: InvoicesRoutes.create, element: <CreateInvoice />, showInSidebar: false },
  {
    path: '/invoices/correct/:baseInvoiceId',
    element: <CreateCorrectionInvoice />,
    showInSidebar: false,
  },
  { path: InvoicesRoutes.update, element: <EditInvoice />, showInSidebar: false },

  // Faktury kosztowe (Cost invoices received from suppliers)
  {
    label: 'Faktury kosztowe',
    path: ExpenseInvoicesRoutes.root,
    element: <ExpenseInvoicesList />,
    showInSidebar: true,
  },
  {
    path: ExpenseInvoicesRoutes.create,
    element: <CreateExpenseInvoicePage />,
    showInSidebar: false,
  },
  {
    path: ExpenseInvoicesRoutes.edit,
    element: <EditExpenseInvoicePage />,
    showInSidebar: false,
  },
  // Import routes must come BEFORE :id route to avoid conflict
  { path: ExpenseInvoicesRoutes.import, element: <InvoiceImportPage />, showInSidebar: false },
  {
    path: ExpenseInvoicesRoutes.importHistory,
    element: <ImportHistoryPage />,
    showInSidebar: false,
  },
  {
    path: ExpenseInvoicesRoutes.importJobDetail,
    element: <ImportJobDetailPage />,
    showInSidebar: false,
  },
  // Generic :id route must come LAST
  {
    path: ExpenseInvoicesRoutes.details,
    element: <ExpenseInvoiceDetailsPage />,
    showInSidebar: false,
  },

  // Dokumenty kasowe (KP/KW)
  {
    label: 'Dokumenty kasowe',
    path: CashDocumentsRoutes.root,
    element: <CashDocumentsPage />,
    showInSidebar: true,
  },
  { path: CashDocumentsRoutes.create, element: <CreateCashDocument />, showInSidebar: false },
  { path: CashDocumentsRoutes.update, element: <EditCashDocument />, showInSidebar: false },
  { path: CashDocumentsRoutes.details, element: <CashDocumentDetailPage />, showInSidebar: false },

  // KSeF outgoing (manual-first operator console)
  {
    label: 'KSeF Outgoing',
    path: KsefOutgoingRoutes.root,
    element: <KsefOutgoingConsolePage />,
    showInSidebar: true,
  },

  // User Management (Admin only)
  {
    label: 'Użytkownicy',
    path: UserManagementRoutes.root,
    element: <UserManagementPage />,
    showInSidebar: true,
  },
  // Reports (grouped under a collapsible "Raporty" sidebar section, see SideBar.tsx)
  {
    label: 'Nierozliczone FV',
    path: ReportsRoutes.reservationPaymentReconciliation,
    element: <ReservationPaymentReconciliationPage />,
    showInSidebar: true,
    icon: <ReceiptLongIcon fontSize="small" />,
    sidebarGroup: 'Raporty',
  },
  {
    label: 'Nierozliczone bookingi',
    path: ReportsRoutes.unsettledBookingsAccounting,
    element: <UnsettledBookingsAccountingReportPage />,
    showInSidebar: true,
    icon: <AssignmentLateIcon fontSize="small" />,
    sidebarGroup: 'Raporty',
  },
  {
    label: 'Wpłaty bez faktury',
    path: ReportsRoutes.uninvoicedReservationPayments,
    element: <UninvoicedReservationPaymentsReportPage />,
    showInSidebar: true,
    icon: <PaymentsIcon fontSize="small" />,
    sidebarGroup: 'Raporty',
  },
];

export const routeLinks: RouteLink[] = [
  // Login route (public, no layout)
  {
    path: '/login',
    element: <LoginPage />,
    showInSidebar: false,
  },
  // Public email verification route
  {
    path: AuthRoutes.verifyEmail,
    element: <VerifyEmailPage />,
    showInSidebar: false,
  },
  // Protected change password route (requires auth, no PasswordChangeGuard)
  {
    path: '/change-password',
    element: <ChangePasswordPage />,
    showInSidebar: false,
  },
  // Protected routes with layout
  {
    path: '/',
    element: <Page />, // layout z sidebar
    children: [
      {
        label: 'Dashboard',
        path: '',
        element: <Dashboard />,
        showInSidebar: true,
      },
      ...featureSections,
    ],
  },
];
